import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import {
  FormField,
  mustBeGreaterThan,
  mustBeLessThan,
  notAllowPlusMinusPointSign,
  onlyAllowPositiveInt,
  required,
  useFormStore,
} from '@tom-ui/utils'
import { EquipmentTypes, NamingSchema, YardBlockDto } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { EquipmentTypeAutoComplete } from '@yard/components/AutoCompletes/EquipmentTypeAutoComplete/EquipmentTypeAutoComplete'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { IYardBlockFormData } from '../../models/yard-setup.model'

interface Props extends FormProps {
  yardBlock?: YardBlockDto
  hasOccupancies?: boolean
  onSubmit: (formValues: IYardBlockFormData) => Promise<void> | void
}

const parseAcceptedLengths = (value: string): { acceptedLengths: number[]; isValid: boolean } => {
  const tokens = value
    .split(',')
    .map(length => length.trim())
    .filter(Boolean)

  const acceptedLengths = tokens.map(length => Number(length))
  const isValid = acceptedLengths.every(length => Number.isInteger(length) && length > 0)

  return {
    acceptedLengths: [...new Set(acceptedLengths)],
    isValid,
  }
}

const formatAcceptedLengths = (acceptedLengths?: number[]): string => acceptedLengths?.join(', ') ?? ''

interface AcceptedLengthsFieldProps {
  value?: number[]
  onChange: (value: number[]) => void
  onValidityChange: (isValid: boolean) => void
  label: string
  helperText: string
  invalidText: string
}

const AcceptedLengthsField = ({
  value,
  onChange,
  onValidityChange,
  label,
  helperText,
  invalidText,
}: AcceptedLengthsFieldProps) => {
  const [inputValue, setInputValue] = useState(formatAcceptedLengths(value))
  const [error, setError] = useState<string>()

  const setParsedValue = (rawValue: string) => {
    const parsed = parseAcceptedLengths(rawValue)
    onValidityChange(parsed.isValid)

    if (!parsed.isValid) {
      setError(invalidText)
      return parsed
    }

    setError(undefined)
    onChange(parsed.acceptedLengths)
    return parsed
  }

  const commitValue = () => {
    const parsed = setParsedValue(inputValue)
    if (!parsed.isValid) {
      return
    }

    setInputValue(formatAcceptedLengths(parsed.acceptedLengths))
  }

  return (
    <TextField
      fullWidth
      label={label}
      name='acceptedLengths'
      helperText={error ?? helperText}
      error={!!error}
      value={inputValue}
      onBlur={commitValue}
      onChange={event => {
        const nextValue = event.target.value
        setInputValue(nextValue)
        setParsedValue(nextValue)
      }}
    />
  )
}

export const YardBlockForm = observer(({ id, yardBlock, hasOccupancies, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const formStore = useFormStore<IYardBlockFormData>()
  const [acceptedLengthsValid, setAcceptedLengthsValid] = useState(true)

  const handleSubmit = () => {
    if (formStore.validate() && acceptedLengthsValid) {
      onSubmit(formStore.data)
    }
  }

  return (
    <Grid
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      container
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
      spacing={theme.customSpacing.m}
    >
      <Grid size={12}>
        <FormField
          formStore={formStore}
          name={'name'}
          initialValue={yardBlock?.name ?? ''}
          validate={value => {
            if (!value) return t('fieldIsRequired', 'Field is required')
          }}
        >
          {store => (
            <Stack>
              <TextField
                {...store.props}
                fullWidth
                label={`${t('name', 'Name')}`}
                required
                name='name'
              />
              {store.error && (
                <Typography variant='caption' color='error'>
                  {store.error}
                </Typography>
              )}
            </Stack>
          )}
        </FormField>
      </Grid>

      <Grid size={12}>
        <FormField
          formStore={formStore}
          name={'description'}
          initialValue={yardBlock?.description ?? ''}
        >
          {store => (
            <TextField
              {...store.props}
              fullWidth
              multiline
              minRows={2}
              label={t('description', 'Description')}
              name='description'
            />
          )}
        </FormField>
      </Grid>

      {!yardBlock && (
        <>
          <Grid size={6}>
            <FormField
              formStore={formStore}
              name={'bayNamingSchema'}
              initialValue={NamingSchema.Numeric}
              validate={value => {
                if (!value) return t('fieldIsRequired', 'Field is required')
              }}
            >
              {store => (
                <FormControl fullWidth sx={{ marginTop: '1rem' }} required>
                  <InputLabel>{t('bayNamingSchema', 'Bay naming schema')}</InputLabel>
                  <Select
                    {...store.props}
                    label={t('bayNamingSchema', 'Bay naming schema')}
                    name='bayNamingSchema'
                  >
                    <MenuItem value={NamingSchema.Numeric}>{t('numeric', 'Numeric')}</MenuItem>
                    <MenuItem value={NamingSchema.Alphabetic}>
                      {t('alphabetic', 'Alphabetic')}
                    </MenuItem>
                  </Select>
                  {store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </FormControl>
              )}
            </FormField>
          </Grid>
          <Grid size={6}>
            <FormField
              formStore={formStore}
              name={'rowNamingSchema'}
              initialValue={NamingSchema.Numeric}
              validate={value => {
                if (!value) return t('fieldIsRequired', 'Field is required')
              }}
            >
              {store => (
                <FormControl fullWidth sx={{ marginTop: '1rem' }} required>
                  <InputLabel>{t('rowNamingSchema', 'Row naming schema')}</InputLabel>
                  <Select
                    {...store.props}
                    label={t('rowNamingSchema', 'Row naming schema')}
                    name='rowNamingSchema'
                  >
                    <MenuItem value={NamingSchema.Numeric}>{t('numeric', 'Numeric')}</MenuItem>
                    <MenuItem value={NamingSchema.Alphabetic}>
                      {t('alphabetic', 'Alphabetic')}
                    </MenuItem>
                  </Select>
                  {store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </FormControl>
              )}
            </FormField>
          </Grid>
        </>
      )}

      {!hasOccupancies && (
        <>
          <Grid size={4}>
            <FormField
              formStore={formStore}
              name={'maxBays'}
              initialValue={yardBlock?.maxBays ?? ''}
              validate={value => {
                if (required()(value)) return t('fieldIsRequired', 'Field is required')
                if (mustBeGreaterThan(Number(value), 1))
                  return t('valueMustBeGreaterThan', 'Value must be greater than {min}', { min: 0 })
                if (mustBeLessThan(Number(value), 999))
                  return t('valueMustBeLessThan', 'Value must be less than {max}', { max: 999 })
              }}
            >
              {store => (
                <Stack>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={`${t('maxBays', 'Max bays')}`}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={e => {
                      onlyAllowPositiveInt(e)
                      store.onChange(e)
                    }}
                    required
                    type='number'
                    name='maxBays'
                  />
                  {store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>
          </Grid>
          <Grid size={4}>
            <FormField
              formStore={formStore}
              name={'maxRows'}
              initialValue={yardBlock?.maxRows ?? ''}
              validate={value => {
                if (required()(value)) return t('fieldIsRequired', 'Field is required')
                if (mustBeGreaterThan(Number(value), 1))
                  return t('valueMustBeGreaterThan', 'Value must be greater than {min}', { min: 0 })
                if (mustBeLessThan(Number(value), 999))
                  return t('valueMustBeLessThan', 'Value must be less than {max}', { max: 999 })
              }}
            >
              {store => (
                <Stack>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={`${t('maxRows', 'Max rows')}`}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={e => {
                      onlyAllowPositiveInt(e)
                      store.onChange(e)
                    }}
                    required
                    type='number'
                    name='maxRows'
                  />
                  {store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>
          </Grid>
          <Grid size={4}>
            <FormField
              formStore={formStore}
              name={'maxTiers'}
              initialValue={yardBlock?.maxTiers ?? ''}
              validate={value => {
                if (required()(value)) return t('fieldIsRequired', 'Field is required')
                if (mustBeGreaterThan(Number(value), 1))
                  return t('valueMustBeGreaterThan', 'Value must be greater than {min}', { min: 0 })
                if (mustBeLessThan(Number(value), 999))
                  return t('valueMustBeLessThan', 'Value must be less than {max}', { max: 999 })
              }}
            >
              {store => (
                <Stack>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={`${t('maxTiers', 'Max tiers')}`}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={e => {
                      onlyAllowPositiveInt(e)
                      store.onChange(e)
                    }}
                    required
                    type='number'
                    name='maxTiers'
                  />
                  {store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>
          </Grid>
        </>
      )}

      {!yardBlock && (
        <Grid size={8}>
          <FormField<IYardBlockFormData, EquipmentTypes[] | undefined>
            formStore={formStore}
            name={'equipmentTypes'}
            initialValue={undefined}
          >
            {store => (
              <FormControl fullWidth>
                <EquipmentTypeAutoComplete multiple onChange={store.setValue} />
              </FormControl>
            )}
          </FormField>
        </Grid>
      )}

      <Grid size={12}>
        <FormField<IYardBlockFormData, number[]>
          formStore={formStore}
          name={'acceptedLengths'}
          initialValue={yardBlock?.acceptedLengths ?? []}
        >
          {store => (
            <AcceptedLengthsField
              label={t('acceptedLengths', 'Accepted lengths')}
              helperText={t('acceptedLengthsHelp', 'Comma-separated container lengths. Leave empty for Any.')}
              invalidText={t('acceptedLengthsInvalid', 'Use comma-separated positive whole numbers.')}
              value={store.value}
              onChange={store.setValue}
              onValidityChange={setAcceptedLengthsValid}
            />
          )}
        </FormField>
      </Grid>
    </Grid>
  )
})
